import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const ProfileSettingsPage = () => {
  const { user, token, isLoading, logout, updateUserInContext } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    phone: '',
    contactStreet: '',
    contactCity: '',
    contactZip: '',
    contactApartment: ''
  });
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  useEffect(() => {
    if (!isLoading && !user) {
      navigate('/login');
    } else if (user) {
      setFormData({
        username: user.username || '',
        email: user.email || '',
        phone: user.phone || '',
        contactStreet: user.contactStreet || '',
        contactCity: user.contactCity || '',
        contactZip: user.contactZip || '',
        contactApartment: user.contactApartment || ''
      });
    }
  }, [user, isLoading, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setIsSavingProfile(true);

    const { email, ...updateData } = formData;

    try {
      const response = await fetch('/api/users/profile', {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData),
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || 'Failed to update profile.');
      }
      
      setMessage('Profile updated successfully!');
      updateUserInContext(responseData);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setIsChangingPassword(true);

    if (newPassword !== confirmNewPassword) {
      setError('New passwords do not match.');
      setIsChangingPassword(false);
      return;
    }
    if (!currentPassword || !newPassword) {
      setError('All password fields are required.');
      setIsChangingPassword(false);
      return;
    }

    console.log("Change password with:", { currentPassword, newPassword });
    setMessage('Password change functionality to be implemented.');
    setTimeout(() => {
        setIsChangingPassword(false);
    }, 1000);
  };
  
  if (isLoading || !user) {
    return <div className="p-8"><p>Loading...</p></div>;
  }

  return (
    <div className="container mx-auto p-4 md:p-8 max-w-2xl">
      <h1 className="text-3xl font-bold text-brand-text-dark mb-8">Profile Settings</h1>

      {message && <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">{message}</div>}
      {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{error}</div>}

      <form onSubmit={handleUpdateProfile} className="bg-white shadow-md rounded-lg p-6 mb-8">
        <h2 className="text-xl font-semibold text-brand-text-dark mb-6">Update Your Information</h2>
        <div className="space-y-4">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700">Username</label>
            <input type="text" name="username" id="username" value={formData.username} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-accent-orange focus:border-brand-accent-orange sm:text-sm" />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email (Cannot be changed)</label>
            <input type="email" name="email" id="email" value={formData.email} readOnly className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 focus:outline-none sm:text-sm" />
          </div>
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Phone (Optional)</label>
            <input type="tel" name="phone" id="phone" value={formData.phone} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-accent-orange focus:border-brand-accent-orange sm:text-sm" />
          </div>

          {/* Service Address Fields */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 class="text-lg font-medium leading-6 text-gray-900">Service Address</h3>
            <div className="mt-4 grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-4">
              <div className="sm:col-span-2">
                <label htmlFor="contactStreet" className="block text-sm font-medium text-gray-700">Street Address</label>
                <input type="text" name="contactStreet" id="contactStreet" value={formData.contactStreet} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-accent-orange focus:border-brand-accent-orange sm:text-sm" />
              </div>
              <div>
                <label htmlFor="contactCity" className="block text-sm font-medium text-gray-700">City</label>
                <input type="text" name="contactCity" id="contactCity" value={formData.contactCity} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-accent-orange focus:border-brand-accent-orange sm:text-sm" />
              </div>
              <div>
                <label htmlFor="contactZip" className="block text-sm font-medium text-gray-700">ZIP Code</label>
                <input type="text" name="contactZip" id="contactZip" value={formData.contactZip} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-accent-orange focus:border-brand-accent-orange sm:text-sm" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="contactApartment" className="block text-sm font-medium text-gray-700">Apartment, suite, unit, etc. (Optional)</label>
                <input type="text" name="contactApartment" id="contactApartment" value={formData.contactApartment} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-accent-orange focus:border-brand-accent-orange sm:text-sm" />
              </div>
            </div>
          </div>

        </div>
        <div className="mt-6 flex justify-end">
          <button type="submit" className="cta-button" disabled={isSavingProfile}>
            {isSavingProfile ? 'Saving...' : 'Save Profile Changes'}
          </button>
        </div>
      </form>

      <form onSubmit={handleChangePassword} className="bg-white shadow-md rounded-lg p-6">
        <h2 className="text-xl font-semibold text-brand-text-dark mb-6">Change Your Password</h2>
        <div className="space-y-4">
          <div>
            <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">Current Password</label>
            <input type="password" name="currentPassword" id="currentPassword" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-accent-orange focus:border-brand-accent-orange sm:text-sm" />
          </div>
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">New Password</label>
            <input type="password" name="newPassword" id="newPassword" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-accent-orange focus:border-brand-accent-orange sm:text-sm" />
          </div>
          <div>
            <label htmlFor="confirmNewPassword" className="block text-sm font-medium text-gray-700">Confirm New Password</label>
            <input type="password" name="confirmNewPassword" id="confirmNewPassword" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-accent-orange focus:border-brand-accent-orange sm:text-sm" />
          </div>
        </div>
        <div className="mt-6 flex justify-end">
        <button type="submit" className="cta-button" disabled={isChangingPassword}>
            {isChangingPassword ? 'Changing...' : 'Change Password'}
          </button>
        </div>
      </form>

      <div className="mt-12 text-center">
        <button class="cta-button"
          onClick={() => logout()} 
        >
          Logout from this device
        </button>
      </div>
    </div>
  );
};

export default ProfileSettingsPage; 